// Danger-zone disclosure for irreversible destructive actions on a
// transcript. Per DECISIONS.md 2026-05-20 "Irreversibility safety
// pattern":
//
//   - Destructive actions (Resubmit; any future "wipe / reset" surfaces)
//     are visually demoted from the primary action row into a disclosure
//     here so they are no longer one-click-from-default.
//   - Confirmation dialog enumerates exactly what gets wiped before the
//     user can proceed. Distinct button copy ("Yes, erase and start over")
//     rather than typed-confirmation -- equivalent safety signal with
//     less friction for the user's actual workflow.
//   - On confirm: the SPA reads the current overlay state, INSERTs a
//     resubmit_initiated event with the full snapshot payload, and only
//     calls /api/resubmit if the event write succeeded. The snapshot IS
//     the safety mechanism -- per Slice 1 Q-B, a failed snapshot write
//     refuses the resubmit. No restore-from-snapshot UI ships in Slice 1,
//     but the data is preserved in the event log for any future feature.

import { useState } from "react";
import { resubmitMeeting } from "../lib/api";
import { logUserEvent } from "../lib/events";
import { supabase } from "../lib/supabase";

interface DangerZoneProps {
  meetingId: string;
  workspaceId: string;
  meetingStatus: string;
  isLocked: boolean;
  // Optimistic-update callback the parent uses to flip status to
  // 'uploaded' immediately after the resubmit fires (matches the prior
  // sticky-header Resubmit handler's behavior).
  onResubmitStart: () => string;
  // Called on resubmit success. The parent reconciles the prevStatus
  // from onResubmitStart on failure.
  onResubmitError: (prevStatus: string, error: unknown) => void;
}

// Helper: keep canResubmit logic local since the legacy version lived
// inline in MeetingReview. ready / error / annotating are the same
// transitional / recovery states the original gate allowed.
function canResubmitStatus(status: string): boolean {
  return status === "ready" || status === "error" || status === "annotating";
}

export function DangerZone({
  meetingId,
  workspaceId,
  meetingStatus,
  isLocked,
  onResubmitStart,
  onResubmitError,
}: DangerZoneProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canActOnStatus = canResubmitStatus(meetingStatus);
  // When locked, the user has to unlock first -- we still SHOW the
  // disclosure so users learn the action exists, but the button is
  // disabled with a plain-language tooltip.
  const disabledReason = isLocked
    ? "Unlock the transcript first."
    : !canActOnStatus
      ? `Cannot start over while status is ${meetingStatus}.`
      : null;

  const fireResubmit = async () => {
    setBusy(true);
    setError(null);
    // 1. Read current overlay state. Re-fetch (don't trust React state)
    //    so the snapshot captures the absolute latest -- Realtime may be
    //    slightly behind a just-made manual edit.
    const [tx, corr, mt] = await Promise.all([
      supabase
        .from("transcripts")
        .select("speaker_map, span_attributions")
        .eq("meeting_id", meetingId)
        .maybeSingle(),
      supabase
        .from("corrections")
        .select(
          "id, meeting_id, workspace_id, flag_id, start_ms, end_ms, " +
            "original_text, corrected_text, promote_to_glossary, status, created_at, pre_correction_status",
        )
        .eq("meeting_id", meetingId),
      supabase
        .from("meetings")
        .select("title, diarization_flags")
        .eq("id", meetingId)
        .maybeSingle(),
    ]);
    if (tx.error) {
      setError(`couldn't read transcripts: ${tx.error.message}`);
      setBusy(false);
      return;
    }
    if (corr.error) {
      setError(`couldn't read corrections: ${corr.error.message}`);
      setBusy(false);
      return;
    }
    if (mt.error) {
      setError(`couldn't read meeting: ${mt.error.message}`);
      setBusy(false);
      return;
    }

    const snapshot = {
      corrections: corr.data ?? [],
      span_attributions: tx.data?.span_attributions ?? [],
      speaker_map: tx.data?.speaker_map ?? {},
      diarization_flags: mt.data?.diarization_flags ?? [],
      title: mt.data?.title ?? null,
    };

    // 2. Write resubmit_initiated event WITH snapshot. If this fails,
    //    REFUSE to fire the resubmit -- the snapshot IS the safety
    //    mechanism (Slice 1 Q-B). Surface the error and let the user
    //    decide whether to retry.
    try {
      await logUserEvent({
        meetingId,
        workspaceId,
        eventType: "resubmit_initiated",
        payload: { snapshot },
      });
    } catch (e) {
      setError(
        `couldn't save a safety snapshot of your current work -- resubmit not fired. ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
      setBusy(false);
      return;
    }

    // 3. Fire the resubmit. Optimistic status flip via the parent.
    const prevStatus = onResubmitStart();
    try {
      await resubmitMeeting(meetingId);
      setDialogOpen(false);
    } catch (e) {
      onResubmitError(prevStatus, e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <details>
        <summary
          style={{
            cursor: "pointer",
            color: "var(--text-muted)",
            fontSize: 14,
            userSelect: "none",
          }}
        >
          Danger zone — destructive actions
        </summary>
        <div
          className="card"
          style={{
            marginTop: 8,
            borderColor: "var(--danger, #dc2626)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>Start over</strong> throws away the current transcript
            and re-transcribes the original audio (~$0.14). All speaker
            labels, text corrections, speaker assignments, and AI
            suggestions on this transcript are erased permanently. Only
            do this if the current transcript is so broken that starting
            over is faster than fixing it by hand.{" "}
            When the analysis reports the transcription itself came back broken, that warning is the legitimate trigger for this.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              type="button"
              disabled={disabledReason !== null || busy}
              onClick={() => setDialogOpen(true)}
              title={disabledReason ?? undefined}
              style={{
                background: "var(--danger, #dc2626)",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: 4,
                cursor:
                  disabledReason !== null || busy ? "not-allowed" : "pointer",
                opacity: disabledReason !== null || busy ? 0.6 : 1,
              }}
            >
              Start over (~$0.14)
            </button>
            {error && (
              <span
                role="alert"
                style={{ color: "var(--danger, #dc2626)", fontSize: 14 }}
              >
                {error}
              </span>
            )}
          </div>
        </div>
      </details>

      {dialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="resubmit-dialog-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={(e) => {
            // Click on backdrop closes; click inside the card doesn't.
            if (e.target === e.currentTarget && !busy) setDialogOpen(false);
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: 520,
              padding: 20,
              borderColor: "var(--danger, #dc2626)",
            }}
          >
            <h3 id="resubmit-dialog-title" style={{ marginTop: 0 }}>
              Erase this transcript and start over?
            </h3>
            <p style={{ fontSize: 14 }}>
              Starting over re-runs transcription on the original audio (~$0.14). The
              following are erased and cannot be recovered automatically:
            </p>
            <ul style={{ fontSize: 14, marginTop: 0 }}>
              <li>Manual speaker label changes</li>
              <li>Text corrections you typed</li>
              <li>Speaker attributions you set on parts of the transcript</li>
              <li>Claude suggestions you accepted or dismissed</li>
            </ul>
            <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
              A snapshot of your current work is saved to the activity
              log before the resubmit fires, so a future restore feature
              could bring it back — but no restore UI exists today. Treat
              this as permanent.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 16,
              }}
            >
              <button
                type="button"
                className="secondary"
                onClick={() => setDialogOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={fireResubmit}
                disabled={busy}
                style={{
                  background: "var(--danger, #dc2626)",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: 4,
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? "Saving snapshot…" : "Yes, erase and start over"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
